import { type FC } from "react";
import {
  Checkbox,
  Typography,
  Card,
  Grid,
  Box,
  Stack,
  IconButton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { TagsItem } from "@/partner/entities/tags";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  overflow: "visible",
  position: "relative",
}));

interface TagsListItemProps {
  tag: TagsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const TagsListItem: FC<TagsListItemProps> = ({
  tag,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();

  return (
    <StyledCard selected={selected} elevation={0}>
      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <Grid size="auto">
          <Checkbox
            color="default"
            checked={selected}
            onChange={(event) => onSelectOneChange(tag.id, event)}
            onClick={(event) => event.stopPropagation()}
          />
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              ml: { xs: 1.5, md: 0 },
              mr: { xs: 1.5, md: 0 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <ItemTitleLink
                  to={`${PagePaths.tags}?tab=edit&id=${tag.id}`}
                  onOpen={() => onClickItem(tag.id)}
                  underline="hover"
                  variant="h6"
                  color="textPrimary"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
                  }}
                >
                  {tag.title}
                </ItemTitleLink>

                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {tag.type?.title ?? t("common.emptyDash")}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid size="auto" container justifyContent="flex-end">
          <Box>
            <Typography variant="body2" color="textPrimary" align="right">
              {t("tags.item.createdAtTime", { createdAt: tag.createdAt })}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="right">
              {t("tags.item.createdAtDate", { createdAt: tag.createdAt })}
            </Typography>
          </Box>
        </Grid>

        <Grid size="auto">
          <Stack direction="row" alignItems="center" spacing={1}>
            {tag.url && (
              <IconButton
                target="_blank"
                href={tag.url}
                size="large"
                component="a"
              >
                <OpenInNewIcon />
              </IconButton>
            )}
          </Stack>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
